import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import {
  FaBuilding,
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaSearch,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaUserCheck,
  FaExclamationTriangle,
  FaCog,
  FaMapMarkerAlt,
  FaCodeBranch
} from 'react-icons/fa';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { toast } from "react-toastify";
import "./CreateCustomerPage.css";

/**
 * EmployeeDepartment — SaaS Production Version
 *
 * DB schema per record:
 *   Company-level: { _id, tenant_id, company_id, company_name, department_name }
 *   Branch-level:  { _id, tenant_id, company_id, company_name, branch_id, branch_name, department_name }
 *
 * The frontend groups multiple DB rows with the same department_name into one UI row.
 */
const EmployeeDepartment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);

  // ─── Data ────────────────────────────────────────────────────────────────────
  const [departments, setDepartments] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [branchesMap, setBranchesMap] = useState({});     // { "CompanyName": ["Branch1", "Branch2"] }
  const [availableBranches, setAvailableBranches] = useState([]);

  // ─── Auth / Permissions ───────────────────────────────────────────────────────
  const [permissions, setPermissions] = useState({ canWrite: false, canDelete: false });
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);

  // ─── UI ───────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [warningMessage, setWarningMessage] = useState('');
  const [warningType, setWarningType] = useState('success');

  // ─── Form ─────────────────────────────────────────────────────────────────────
  const [doctypeFields, setDoctypeFields] = useState([]);
  const [formData, setFormData] = useState({ department_name: '', is_active: true, description: '' });
  const [editingId, setEditingId] = useState(null);

  // Multi-tenant selection in form
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);   // branch names chosen in form
  const [formBranches, setFormBranches] = useState([]);            // branches available for selected companies

  // ─── Filters (list view) ──────────────────────────────────────────────────────
  const [selectedViewCompany, setSelectedViewCompany] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');

  // ─── Navigation origin ────────────────────────────────────────────────────────
  const [fromDesignation, setFromDesignation] = useState(location.state?.fromDesignation || false);
  const [fromAddEmployee, setFromAddEmployee] = useState(location.state?.fromAddEmployee || false);

  // ─────────────────────────────────────────────────────────────────────────────
  //  INIT
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (configLoading) return;
    const url = baseUrl || '';
    const init = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchPermissions(url),
          fetchDepartments(url),
          fetchMetadata(url),
          fetchAllBranches(url),
        ]);
      } catch (err) {
        toast.error('Failed to load department data.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [baseUrl, configLoading]);

  // Handle navigation state
  useEffect(() => {
    if (location.state?.fromDesignation) setFromDesignation(true);
    if (location.state?.fromAddEmployee) setFromAddEmployee(true);
    if (location.state?.fromDesignation || location.state?.fromAddEmployee) {
      setShowForm(true);
    }
  }, [location.state]);

  // When selected companies change → refresh form branches
  useEffect(() => {
    const branches = [];
    selectedCompanies.forEach(comp => {
      const b = branchesMap[comp] || [];
      b.forEach(br => { if (!branches.includes(br)) branches.push(br); });
    });
    setFormBranches(branches);
    // Remove any selected branches that no longer belong to chosen companies
    setSelectedBranches(prev => prev.filter(b => branches.includes(b)));
  }, [selectedCompanies, branchesMap]);



  // ─────────────────────────────────────────────────────────────────────────────
  //  DATA FETCHERS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchDepartments = async (url) => {
    try {
      const res = await axios.get(`${url}/api/departments`, { headers: getHeaders() });
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Fetch Departments Error:', err);
    }
  };

  const fetchMetadata = async (url) => {
    try {
      const res = await axios.get(`${url}/api/doctypes/${encodeURIComponent('Employee Department')}`, { headers: getHeaders() });
      const fields = res.data.fields || [];
      setDoctypeFields(fields);
    } catch {
      setDoctypeFields([
        { id: 'department_name', label: 'Department Name', type: 'Data', mandatory: true },
        { id: 'is_active', label: 'Active', type: 'Check' },
        { id: 'description', label: 'Description', type: 'Text' }
      ]);
    }
  };

  const fetchPermissions = async (url) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const roleRaw = (user.role || user.UserType || '').toLowerCase().replace(' ', '_');
      const branch = user.branch_name || user.branch || '';

      const isGroup = ['group_admin', 'groupadmin', 'tenant_admin', 'super_admin'].includes(roleRaw);
      const isAdmin = ['company_admin', 'admin', 'group_admin', 'groupadmin', 'tenant_admin', 'super_admin'].includes(roleRaw) &&
        (!branch || branch === 'All Branches' || branch === '');

      setIsGroupAdmin(isGroup);
      setIsCompanyAdmin(isAdmin);

      // Build companyOptions
      let comps = [];
      if (user.companies && user.companies.length > 0) {
        comps = user.companies.map(c =>
          typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c
        ).filter(Boolean);
      } else if (user.company_name || user.company) {
        comps = [user.company_name || user.company];
      } else {
        try {
          const res = await axios.get(`${url}/api/company-details`, { headers: getHeaders() });
          comps = (res.data.companyDetails || []).map(d => d.restaurantName || d.company_name).filter(Boolean);
        } catch { /* ignore */ }
      }
      const uniqueComps = [...new Set(comps)];
      setCompanyOptions(uniqueComps);

      // Pre-select company
      const activeCtx = localStorage.getItem('active_company');
      if (activeCtx && activeCtx !== 'All') {
        setSelectedCompanies([activeCtx]);
      } else {
        setSelectedCompanies(uniqueComps.length === 1 ? [uniqueComps[0]] : []);
      }

      // Permissions
      if (['company_admin', 'admin', 'branch_admin', 'group_admin', 'tenant_admin', 'master_admin', 'owner'].includes(roleRaw)) {
        setPermissions({ canWrite: true, canDelete: true });
      } else {
        try {
          const res = await axios.get(`${url}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`, { headers: getHeaders() });
          const pagePerm = (res.data.permissions || []).find(p => p.pageId === 'employee_departments');
          if (pagePerm) setPermissions({ canWrite: !!pagePerm.canWrite, canDelete: !!pagePerm.canDelete });
        } catch { /* ignore */ }
      }
    } catch (err) {
      console.warn('Permissions error:', err);
    }
  };

  const fetchAllBranches = async (url) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const activeCtx = localStorage.getItem('active_company');

      // Build a comma-separated list of companies to fetch branches for
      let compsToFetch = [];
      if (activeCtx && activeCtx !== 'All') {
        compsToFetch = [activeCtx];
      } else if (userObj.companies && userObj.companies.length > 0) {
        compsToFetch = userObj.companies.map(c =>
          typeof c === 'object' ? (c.company_name || c.company || '') : c
        ).filter(Boolean);
      } else if (userObj.company_name || userObj.company) {
        compsToFetch = [userObj.company_name || userObj.company];
      }

      if (!compsToFetch.length) return;

      const map = {};
      const allNames = [];

      await Promise.all(compsToFetch.map(async comp => {
        try {
          const res = await axios.get(
            `${url}/api/branches?company_name=${encodeURIComponent(comp)}`,
            { headers: { ...getHeaders(), 'X-Company-Name': comp } }
          );
          const names = (res.data || []).map(b =>
            typeof b === 'string' ? b : (b.branch_name || b.name || '')
          ).filter(Boolean);
          map[comp] = names;
          names.forEach(n => { if (!allNames.includes(n)) allNames.push(n); });
        } catch (e) { console.warn('Branch fetch error for', comp, e); }
      }));

      setBranchesMap(map);
      setAvailableBranches(allNames);
    } catch (err) {
      console.error('Fetch branches error:', err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  //  FORM HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleCompany = (comp) => {
    setSelectedCompanies(prev =>
      prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]
    );
  };

  const toggleBranch = (brName) => {
    setSelectedBranches(prev =>
      prev.includes(brName) ? prev.filter(b => b !== brName) : [...prev, brName]
    );
  };

  const resetForm = () => {
    setFormData({ department_name: '', is_active: true, description: '' });
    setEditingId(null);
    setSelectedBranches([]);
    const activeCtx = localStorage.getItem('active_company');
    if (activeCtx && activeCtx !== 'All') {
      setSelectedCompanies([activeCtx]);
    } else {
      setSelectedCompanies(companyOptions.length === 1 ? [companyOptions[0]] : []);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!permissions.canWrite) {
      toast.error('Permission Denied: You cannot create or edit departments.');
      return;
    }
    if (!formData.department_name?.trim()) {
      toast.error('Department Name is required.');
      return;
    }

    // Determine companies to save
    const activeCtx = localStorage.getItem('active_company');
    const userStr = localStorage.getItem('user');
    const userData = userStr ? JSON.parse(userStr) : {};
    const userComp = userData.company_name || userData.company || '';
    const companiesToProcess = activeCtx === 'All'
      ? selectedCompanies
      : [activeCtx || userComp || ''].filter(Boolean);

    if (!companiesToProcess.length) {
      toast.error('Please select at least one company.');
      return;
    }

    // Determine branches
    const activeBranchLocal = localStorage.getItem('active_branch');
    const isSpecificBranch = activeBranchLocal &&
      activeBranchLocal !== 'All Branches' &&
      activeBranchLocal !== 'All' &&
      activeBranchLocal !== '';
    const branchesToProcess = isSpecificBranch ? [activeBranchLocal] : selectedBranches;

    setLoading(true);

    try {
      const url = baseUrl || '';
      const headers = getHeaders();
      if (companiesToProcess[0]) headers['X-Company-Name'] = companiesToProcess[0];
      if (branchesToProcess[0]) headers['X-Branch-Name'] = branchesToProcess[0];

      const payload = {
        name: formData.department_name.trim(),
        department_name: formData.department_name.trim(),
        is_active: formData.is_active !== false,
        description: formData.description || '',
        company_names: companiesToProcess,
        company_name: companiesToProcess[0] || '',
        branch_names: branchesToProcess,
        branch_name: branchesToProcess[0] || ''
      };

      if (editingId) {
        const itemToEdit = departments.find(d => (d._id || d.id) === editingId);
        if (itemToEdit?.global_ref_id) {
          await axios.put(`${url}/api/departments/bulk/${itemToEdit.global_ref_id}`, payload, { headers });
        } else {
          await axios.put(`${url}/api/departments/${editingId}`, payload, { headers });
        }
        setWarningMessage('Department updated successfully!');
      } else {
        await axios.post(`${url}/api/departments`, payload, { headers });
        setWarningMessage('Department created successfully!');
      }

      setWarningType('success');
      await fetchDepartments(url);

    } catch (err) {
      setWarningMessage(err.response?.data?.error || 'Failed to save department.');
      setWarningType('error');
    } finally {
      setLoading(false);
    }
  };

  const initiateEdit = (dept) => {
    let relatedDepts = [dept];
    if (dept.global_ref_id) {
      relatedDepts = departments.filter(d => d.global_ref_id === dept.global_ref_id);
    }

    setEditingId(dept._id || dept.id);
    setFormData({
      department_name: dept.department_name || dept.name || '',
      is_active: dept.is_active !== false,
      description: dept.description || ''
    });

    const comps = new Set();
    const brs = new Set();

    relatedDepts.forEach(t => {
      if (t.company_names && Array.isArray(t.company_names)) t.company_names.forEach(c => comps.add(c));
      else if (t.company_name) comps.add(t.company_name);

      if (t.branch_names && Array.isArray(t.branch_names)) t.branch_names.forEach(b => brs.add(b));
      else if (t.branch_names && typeof t.branch_names === 'string') t.branch_names.split(',').forEach(b => brs.add(b.trim()));
      else if (t.branch_name) brs.add(t.branch_name);
      else if (t.branch) brs.add(t.branch);
    });

    setSelectedCompanies(Array.from(comps));
    setSelectedBranches(Array.from(brs));
    setShowForm(true);
  };

  const executeDelete = async () => {
    if (!permissions.canDelete) {
      toast.error('Permission Denied: You cannot delete.');
      setShowDeleteConfirm(false);
      return;
    }
    try {
      const url = baseUrl || '';
      const endpoint = `${url}/api/departments/${deleteId}`;
      await axios.delete(endpoint, { headers: getHeaders() });
      toast.warn('Department deleted successfully.');
      setDepartments(prev => prev.filter(d => d._id !== deleteId));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete department.');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleBack = () => {
    if (fromDesignation) {
      navigate('/employee-designations', { state: location.state?.originalAddEmployeeState || location.state });
    } else if (fromAddEmployee) {
      navigate('/add-employee', { state: location.state?.originalAddEmployeeState || location.state });
    } else if (showForm) {
      setShowForm(false);
      resetForm();
    } else {
      navigate('/admin');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  //  FILTERED LIST
  // ─────────────────────────────────────────────────────────────────────────────
  const filteredDepts = departments.filter(d => {
    const matchSearch = (d.department_name || d.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchComp = !selectedViewCompany || (d.company_name || d.company) === selectedViewCompany;
    const matchBranch = !selectedBranchFilter || (d.branch_name || d.branch) === selectedBranchFilter;
    return matchSearch && matchComp && matchBranch;
  });

  // ─────────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading && !departments.length && !showForm) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <div style={{ color: '#555', fontFamily: 'sans-serif' }}>Loading Departments...</div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="create-customer-container main-customer-page">
      {/* Header */}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={handleBack} className="header-back-btn">
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1><FaBuilding style={{marginRight: '12px', color: '#604BE8'}} /> Departments</h1>
        </div>
        <div className="header-right">
          <div className="header-actions">
            {(isGroupAdmin || isCompanyAdmin) && (
              <button onClick={() => setShowCustomizeModal(true)} className="tab-btn" style={{ background: '#3b82f6' }}>
                <FaCog /> Customize
              </button>
            )}
            {!showForm && permissions.canWrite && (
              <button onClick={() => { resetForm(); setShowForm(true); }} className="add-btn">
                <FaPlus /> Add New
              </button>
            )}
            {showForm && (
              <>
                <button type="button" className="danger-btn" onClick={handleBack} style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }}>
                  Cancel
                </button>
                <button type="submit" form="department-form" disabled={loading} className="save-btn">
                  <FaSave /> {loading ? 'Processing...' : (editingId ? 'Update' : 'Save')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper" style={{ maxWidth: '1200px', width: '100%' }}>
          {showForm ? (
            /* ══════════════ FORM ══════════════ */
            <form id="department-form" onSubmit={handleSubmit} className="form-section-card theme-blue">
              <div className="section-header">
                <h2>{editingId ? "Edit Department :" : "New Department :"}</h2>
              </div>
              <div className="form-grid" style={{ marginBottom: '30px' }}>
                {doctypeFields.filter(f => !f.hidden).map(field => {
                  const type = (field.type || '').toLowerCase();
                  const isDisabled = field.disabled || field.read_only;
                  return (
                    <div key={field.id} className="form-group">
                      {type === 'check' ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', marginTop: '28px', background: '#F3F6FB', padding: '12px 16px', borderRadius: '8px', border: '1px solid transparent' }}>
                          <input
                            type="checkbox"
                            name={field.id}
                            checked={!!formData[field.id]}
                            onChange={handleInputChange}
                            disabled={isDisabled}
                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#604BE8', margin: 0 }}
                          />
                          <span style={{ fontWeight: '600', color: '#1B1B29' }}>{field.label}</span>
                        </label>
                      ) : (
                        <>
                          <label>
                            {field.label} {field.mandatory && <span className="required">*</span>}
                          </label>
                          {type === 'text' ? (
                            <textarea
                              name={field.id}
                              value={formData[field.id] || ''}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              placeholder={field.placeholder || `Enter ${field.label}`}
                              style={{ width: '100%', minHeight: '100px', padding: '12px 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none', resize: 'vertical' }}
                            />
                          ) : (
                            <input
                              type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'}
                              name={field.id}
                              value={formData[field.id] || ''}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              placeholder={field.placeholder || `Enter ${field.label}`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Company Assignment ── */}
              {companyOptions.length > 0 && (
                <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '15px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '15px', fontWeight: '700', color: '#1B1B29' }}>Assign to Companies *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                      {companyOptions.map((comp, idx) => {
                        const isSel = selectedCompanies.includes(comp);
                        return (
                          <label key={idx}
                            onClick={() => toggleCompany(comp)}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: isSel ? '#eff6ff' : '#eff2f9', borderRadius: '12px', border: `1.5px solid ${isSel ? '#3b82f6' : '#C3CDE4'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                            <input type="checkbox" checked={isSel} readOnly
                              style={{ width: '18px', height: '18px', accentColor: '#604BE8', cursor: 'pointer' }} />
                            {comp}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Branch Assignment ── */}
              {(isGroupAdmin || isCompanyAdmin) && formBranches.length > 0 && (
                <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '15px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', color: '#1B1B29' }}>Assign to Branches</label>
                    <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                      Select branches to create branch-level department records.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                      {formBranches.map((br, idx) => {
                        const isSel = selectedBranches.includes(br);
                        return (
                          <label key={idx}
                            onClick={() => toggleBranch(br)}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: isSel ? '#ecfdf5' : '#eff2f9', borderRadius: '12px', border: `1.5px solid ${isSel ? '#d1fae5' : '#C3CDE4'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                            <input type="checkbox" checked={isSel} readOnly
                              style={{ width: '18px', height: '18px', accentColor: '#06D6A0', cursor: 'pointer' }} />
                            {br}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Submit Buttons Removed (Moved to Header) ── */}
            </form>
          ) : (
            /* ══════════════ LIST ══════════════ */
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ marginBottom: '20px' }}>
                <h2>Departments List</h2>
              </div>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                  <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="text" placeholder="Search departments..."
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    style={{ width: '100%', height: '44px', padding: '0 16px 0 45px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none', transition: 'all 0.3s ease', boxSizing: 'border-box' }} onFocus={(e) => e.target.style.borderColor = '#604BE8'} onBlur={(e) => e.target.style.borderColor = 'transparent'} />
                </div>

                {companyOptions.length > 1 && (
                  <div style={{ width: '200px' }}>
                    <select value={selectedViewCompany} onChange={e => setSelectedViewCompany(e.target.value)}
                      style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none' }}>
                      <option value="">All Companies</option>
                      {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}

                {availableBranches.length > 0 && (!localStorage.getItem('active_branch') || localStorage.getItem('active_branch') === 'All Branches') && (
                  <div style={{ width: '200px' }}>
                    <select value={selectedBranchFilter} onChange={e => setSelectedBranchFilter(e.target.value)}
                      style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none' }}>
                      <option value="">All Branches</option>
                      {availableBranches.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Table */}
              <div style={{ width: '100%', backgroundColor: '#ffffff', border: 'none' }} className="premium-card">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Department Name</th>
                      <th>Status</th>
                      <th>Company</th>
                      <th>Branch</th>
                      <th style={{ textAlign: 'right', paddingRight: '36px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDepts.map((dept, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: '700', color: '#1B1B29', fontSize: '15px' }}>
                          {dept.department_name || dept.name}
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase',
                            backgroundColor: dept.is_active ? '#dcfce7' : '#fee2e2',
                            color: dept.is_active ? '#10b981' : '#ef4444'
                          }}>
                            {dept.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span style={{ padding: '4px 12px', borderRadius: '6px', background: '#eff2f9', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                            {dept.company_name || dept.company || '—'}
                          </span>
                        </td>
                        <td>
                          <span style={{ padding: '4px 12px', borderRadius: '6px', background: '#eff2f9', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                            {dept.branch_name && dept.branch_name !== 'All Branches' ? dept.branch_name : 'All Branches'}
                          </span>
                        </td>
                        <td style={{ paddingRight: '24px' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button onClick={() => initiateEdit(dept)} className="action-btn action-btn-edit" title="Edit">
                              <FaEdit style={{ fontSize: '20px' }} />
                            </button>
                            <button onClick={() => { setDeleteId(dept._id); setShowDeleteConfirm(true); }} className="action-btn action-btn-delete" title="Delete">
                              <FaTrash style={{ fontSize: '20px' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDepts.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '15px', fontWeight: '600' }}>
                    No departments found.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Delete Confirm Modal ── */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '400px',
            padding: '32px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%', background: '#fff0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto'
            }}>
              <FaTrash style={{ fontSize: '28px', color: '#FF595E' }} />
            </div>
            <h3 style={{ color: '#1B1B29', fontSize: '22px', fontWeight: '700', margin: '0 0 10px 0' }}>Remove Department?</h3>
            <p style={{ color: '#777777', fontSize: '15px', marginBottom: '30px', lineHeight: '1.6', fontWeight: '500' }}>
              Are you sure you want to delete this department? This action is permanent and cannot be reversed.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                Keep Department
              </button>
              <button onClick={executeDelete} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#FF595E', color: '#ffffff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(255, 89, 94, 0.3)' }}>
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warning / Success Modal */}
      {warningMessage && (
        <div className="premium-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div className="premium-modal-content" style={{
            background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '420px', padding: '40px 32px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', position: 'relative', overflow: 'hidden',
            animation: 'slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            <div style={{
              position: 'absolute', top: '-50px', left: '50%', transform: 'translateX(-50%)',
              width: '200px', height: '200px', zIndex: 0, pointerEvents: 'none',
              background: warningType === 'success' ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(255, 159, 4, 0.15) 0%, rgba(255,255,255,0) 70%)'
            }}></div>
            <div className="premium-modal-icon" style={{
              width: '84px', height: '84px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '38px', marginBottom: '24px', zIndex: 1, animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              background: warningType === 'success' ? '#ecfdf5' : '#fffbeb',
              color: warningType === 'success' ? '#06D6A0' : '#FF9F04',
              border: `2px solid ${warningType === 'success' ? '#a7f3d0' : '#fde68a'}`,
              boxShadow: warningType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(255, 159, 4, 0.2)'
            }}>
              {warningType === 'success' ? <FaUserCheck /> : <FaExclamationTriangle />}
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0', zIndex: 1, fontFamily: `'Gilroy', 'Inter', sans-serif` }}>
              {warningType === 'success' ? "Success" : "Notice"}
            </h3>
            <p style={{ fontSize: '16px', color: '#64748b', lineHeight: '1.6', margin: '0 0 32px 0', zIndex: 1, fontWeight: '500' }}>
              {warningMessage}
            </p>
            <div className="premium-modal-actions" style={{ display: 'flex', gap: '16px', width: '100%', zIndex: 1 }}>
              {warningType !== 'success' && (
                <button type="button" className="premium-btn-cancel" onClick={() => setWarningMessage("")}
                  style={{
                    flex: 1, height: '48px', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '14px',
                    fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: `'Gilroy', 'Inter', sans-serif`
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}>
                  Cancel
                </button>
              )}
              <button type="button" className="premium-btn-ok"
                onClick={() => {
                  setWarningMessage("");
                  if (warningType === 'success') {
                    const savedName = formData.department_name?.trim() || "";
                    if (fromDesignation) {
                      navigate('/employee-designations', { state: { ...location.state, departmentCreated: true, newDepartmentName: savedName } });
                    } else if (fromAddEmployee) {
                      navigate('/add-employee', { state: { ...location.state, formData: { ...location.state?.formData, department: savedName } } });
                    } else {
                      setShowForm(false);
                      resetForm();
                    }
                  }
                }}
                style={{
                  flex: 1, height: '48px', color: '#ffffff', border: 'none', borderRadius: '14px', fontWeight: '700', fontSize: '15px',
                  cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: `'Gilroy', 'Inter', sans-serif`,
                  background: warningType === 'success' ? '#06D6A0' : '#604BE8',
                  boxShadow: warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 12px 25px -5px rgba(6, 214, 160, 0.4)' : '0 12px 25px -5px rgba(96, 75, 232, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)'; }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => fetchMetadata(baseUrl || '')}
        targetDocType="Employee Department"
      />
    </div>
  );
};

export default EmployeeDepartment;